// A wiki card on a token (issue 1155), the client's half. The campaign's wiki lives on the site; the server reads it
// with this person's own bearer, so every call here is to THIS origin (`requestJson`, so `authFetch`). A page the site
// keeps from this person, a token with no page and a token they cannot see are all the same answer: none. The page's
// portrait (issue 1541) is a picture of this origin too: the card says there is one, `readWikiPortrait` fetches its bytes.
import { requestJson } from './api';
import { authFetch } from './session';

/** A run of the card's body (issue 1541): text, or a link to a path on the site, opened in the compendium's window. */
export interface CardPart { text: string; href?: string }
/** The page's portrait (issue 1541): that there is one, and its caption; the picture is `readWikiPortrait`'s. */
export interface CardPortrait { caption: string }
export interface WikiCardData { title: string; type: string | null; body: string; url: string; parts?: CardPart[]; portrait?: CardPortrait }
export interface WikiPageRow { id: string; title: string; type: string | null; visibility: 'gm' | 'players' }

type Refused = { kind: 'refused'; reason: string } | { kind: 'error' };
export type CardAnswer = { kind: 'ok'; card: WikiCardData } | { kind: 'none' } | Refused;
export type PagesAnswer = { kind: 'ok'; pages: WikiPageRow[] } | Refused;

const enc = encodeURIComponent;
const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
const refusedOr = (body: unknown): Refused => {
  const reason = (body as { reason?: unknown } | null)?.reason;
  return typeof reason === 'string' ? { kind: 'refused', reason } : { kind: 'error' };
};

/**
 * The body's parts as sent (issue 1541): text runs, and links only to a path on the site; a link anywhere else keeps
 * its words and loses its address. A server that sends none (before 1541) is read as the plain body.
 */
function partsOf(v: unknown, body: string): CardPart[] {
  if (!Array.isArray(v)) return body ? [{ text: body }] : [];
  return v.filter((p): p is { text: string; href?: unknown } => isObj(p) && typeof p.text === 'string')
    .map((p) => (typeof p.href === 'string' && p.href.startsWith('/') && !p.href.startsWith('//') ? { text: p.text, href: p.href } : { text: p.text }));
}

/** The token's card: its page as this person may read it, or none. */
export async function readWikiCard(campaignId: string, objectId: string, fetchImpl: typeof fetch = fetch): Promise<CardAnswer> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/objects/${enc(objectId)}/wiki`, {}, fetchImpl);
  if (status === 404) return { kind: 'none' };
  const b = body as Record<string, unknown> | null;
  if (status === 200 && isObj(b) && typeof b.title === 'string' && (b.type === null || typeof b.type === 'string') && typeof b.body === 'string' && typeof b.url === 'string' && b.url.startsWith('/')) {
    const p = b.portrait as { caption?: unknown } | undefined;
    const portrait = isObj(p) ? { portrait: { caption: typeof p.caption === 'string' ? p.caption : '' } } : {};
    return { kind: 'ok', card: { title: b.title, type: b.type as string | null, body: b.body, url: b.url, parts: partsOf(b.parts, b.body), ...portrait } };
  }
  return refusedOr(body);
}

/** The token's card's portrait as a picture (issue 1541), read through this origin with this person's sign-in; null for none. */
export async function readWikiPortrait(campaignId: string, objectId: string, fetchImpl: typeof fetch = fetch): Promise<Blob | null> {
  try {
    const res = await authFetch(`/api/tables/${enc(campaignId)}/objects/${enc(objectId)}/wiki/portrait`, {}, fetchImpl);
    if (!res.ok || !(res.headers.get('content-type') ?? '').startsWith('image/')) return null;
    return await res.blob();
  } catch {
    return null;
  }
}

/** The campaign's pages, for the DM's picker. */
export async function listWikiPages(campaignId: string, fetchImpl: typeof fetch = fetch): Promise<PagesAnswer> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/wiki-pages`, {}, fetchImpl);
  const pages = (body as { pages?: unknown } | null)?.pages;
  if (status === 200 && Array.isArray(pages)) {
    return { kind: 'ok', pages: pages.filter((p): p is WikiPageRow => isObj(p) && typeof p.id === 'string' && typeof p.title === 'string' && (p.visibility === 'gm' || p.visibility === 'players')) };
  }
  return refusedOr(body);
}
